import React, { useEffect, useState } from 'react';
import { Praxis, Zuweisung, Zeitslot, Abwesenheit, Feiertag } from '../../types';
import { praxisService } from '../../services/praxis.service';
import { zuweisungService } from '../../services/zuweisung.service';
import { abwesenheitService } from '../../services/abwesenheit.service';
import { feiertagService } from '../../services/feiertag.service';
import { scheduleService } from '../../services/schedule.service';
import { formatDate, isSameDay, isDateWithin } from '../../utils/dateUtils';
import { useEnvironment } from '../../context/EnvironmentContext';
import { AppType } from '../../types';

interface WeeklyScheduleReadOnlyProps {
  arztId?: number;
  praxisId?: number;
  refreshTrigger?: number;
  weekStart?: Date;
  onWeekChange?: (date: Date) => void;
}

const WeeklyScheduleReadOnly: React.FC<WeeklyScheduleReadOnlyProps> = ({
  arztId,
  praxisId,
  refreshTrigger,
  weekStart,
  onWeekChange,
}) => {
  const { currentApp } = useEnvironment();
  const [internalWeekStart, setInternalWeekStart] = useState<Date>(getMonday(new Date()));
  const currentWeekStart = weekStart || internalWeekStart;

  const handleWeekChange = (newDate: Date) => {
    if (onWeekChange) {
      onWeekChange(newDate);
    }
    if (!weekStart) {
      setInternalWeekStart(newDate);
    }
  };
  const [praxen, setPraxen] = useState<Praxis[]>([]);
  const [zuweisungen, setZuweisungen] = useState<Zuweisung[]>([]);
  const [abwesenheiten, setAbwesenheiten] = useState<Abwesenheit[]>([]);
  const [feiertage, setFeiertage] = useState<Feiertag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const weekDays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  const hasAfternoonSlot = (day: string) => {
    return !['Mittwoch', 'Freitag', 'Samstag'].includes(day);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart, refreshTrigger, arztId, praxisId, currentApp]);

  function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 5);

      const filter: any = {
        startDatum: currentWeekStart,
        endDatum: weekEnd,
      };

      if (arztId) {
        filter.arztId = arztId;
      }

      if (praxisId) {
        filter.praxisId = praxisId;
      }

      let zuweisungenList: Zuweisung[] = [];
      let praxenList: Praxis[] = [];
      let abwesenheitenList: Abwesenheit[] = [];
      let feiertageList: Feiertag[] = [];

      // 1. Fetch Praxen, Abwesenheiten, Feiertage (App-filtered)
      const [praxenData, abwesenheitenData, feiertageData] = await Promise.all([
        praxisService.getAllPraxen(currentApp as AppType),
        abwesenheitService.getAllAbwesenheiten({
          startDatum: currentWeekStart,
          endDatum: weekEnd,
          app: currentApp as AppType
        }),
        feiertagService.getAllFeiertage({ startDatum: currentWeekStart, endDatum: weekEnd }),
      ]);

      praxenList = praxenData;
      abwesenheitenList = abwesenheitenData;
      feiertageList = feiertageData;

      // 2. Fetch Assignments based on AppType
      if (currentApp === AppType.LEGACY) {
        const [zuweisungenData] = await Promise.all([
          zuweisungService.getAllZuweisungen(filter, 1, 10000),
        ]);
        zuweisungenList = Array.isArray(zuweisungenData) ? zuweisungenData : zuweisungenData.data;
      } else {
        // Modern Architecture
        zuweisungenList = await scheduleService.getEffectiveSchedule({
          app: currentApp as AppType,
          startDatum: currentWeekStart,
          endDatum: weekEnd,
          // Optional: filter by arztId if needed, scheduleService supports filtering internally?
          // Actually getEffectiveSchedule mainly takes date range and app. 
          // If we need to filter by doctor/praxis locally, we can do it after fetch or update service.
          // For now, let's fetch all and let the component's render logic filter (which it does via getZuweisungenForCell).
        });
      }

      setPraxen(praxenList);
      setZuweisungen(zuweisungenList);
      setAbwesenheiten(abwesenheitenList);
      setFeiertage(feiertageList);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const getDateForDay = (dayIndex: number): Date => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + dayIndex);
    return date;
  };

  const getZuweisungenForCell = (praxisId: number, date: Date, zeitslot: Zeitslot): Zuweisung[] => {
    return zuweisungen.filter(
      (z) =>
        z.praxisId === praxisId &&
        isSameDay(z.datum, date) &&
        z.zeitslot === zeitslot
    );
  };

  const handlePreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    handleWeekChange(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    handleWeekChange(newDate);
  };

  const handleCurrentWeek = () => {
    handleWeekChange(getMonday(new Date()));
  };

  const renderCell = (praxisId: number, date: Date, zeitslot: Zeitslot) => {
    const zuweisungenInCell = getZuweisungenForCell(praxisId, date, zeitslot);

    return (
      <div className="px-2 py-2 text-xs min-h-[3rem]">
        {zuweisungenInCell.length > 0 ? (
          <div className="space-y-1">
            {zuweisungenInCell.map((zuweisung) => {
              const isAbsent = abwesenheiten.some(a => a.arztId === zuweisung.arztId && isDateWithin(date, a.startDatum, a.endDatum));

              return (
                <div
                  key={zuweisung.id}
                  className={`px-2 py-1 rounded ${isAbsent ? 'bg-red-100 border border-red-200' : 'bg-gray-100'}`}
                  title={isAbsent ? 'Arzt ist abwesend/im Urlaub' : ''}
                >
                  <span className={`font-medium text-xs ${isAbsent ? 'text-red-800 line-through' : 'text-gray-900'}`}>
                    {zuweisung.arzt
                      ? `${zuweisung.arzt.vorname} ${zuweisung.arzt.nachname}`
                      : 'Unbekannt'}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <span className="text-gray-400">-</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const weekEnd = new Date(currentWeekStart);
  weekEnd.setDate(weekEnd.getDate() + 5);

  // Filter practices to display
  const displayedPraxen = praxisId
    ? praxen.filter((p) => p.id === praxisId)
    : praxen;

  return (
    <div>
      {/* Week Navigation */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handlePreviousWeek}
            className="px-4 py-3 min-h-[44px] bg-gray-200 hover:bg-gray-300 rounded-md transition-colors text-sm md:text-base"
          >
            ← Vorherige
          </button>
          <button
            onClick={handleCurrentWeek}
            className="px-4 py-3 min-h-[44px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors text-sm md:text-base"
          >
            Aktuelle Woche
          </button>
          <button
            onClick={handleNextWeek}
            className="px-4 py-3 min-h-[44px] bg-gray-200 hover:bg-gray-300 rounded-md transition-colors text-sm md:text-base"
          >
            Nächste →
          </button>
        </div>
        <div className="text-base md:text-lg font-semibold text-gray-800">
          {formatDate(currentWeekStart)} - {formatDate(weekEnd)}
        </div>
      </div>

      {/* Mobile View (Cards) */}
      <div className="md:hidden space-y-4 mb-6">
        {displayedPraxen.length === 0 ? (
          <div className="text-center p-4 text-gray-500 bg-white rounded-lg shadow">
            Keine Praxen vorhanden.
          </div>
        ) : (
          displayedPraxen.map((praxis) => (
            <div key={praxis.id} className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 font-bold text-gray-900 flex justify-between items-center">
                <span>{praxis.name}</span>
              </div>
              <div className="divide-y divide-gray-200">
                {weekDays.map((day, dayIndex) => {
                  const date = getDateForDay(dayIndex);

                  const fullDayHoliday = feiertage.find(f => {
                    return isSameDay(f.datum, date) && f.typ === 'GANZTAGS';
                  });
                  const vmHoliday = feiertage.find(f => {
                    return isSameDay(f.datum, date) && f.typ === 'VORMITTAG';
                  });
                  const nmHoliday = feiertage.find(f => {
                    return isSameDay(f.datum, date) && f.typ === 'NACHMITTAG';
                  });

                  return (
                    <div key={`${praxis.id}-${day}`} className="p-3">
                      <div className="font-medium text-sm text-gray-700 mb-2 flex justify-between">
                        <span>{day}</span>
                        <span className="text-gray-500 font-normal">{formatDate(date)}</span>
                      </div>

                      {fullDayHoliday ? (
                        <div className="bg-orange-100 p-2 rounded text-center mb-2">
                          <span className="font-bold text-orange-800 text-sm">
                            {fullDayHoliday.name || 'FEIERTAG'}
                          </span>
                        </div>
                      ) : (
                        <div className={`grid ${hasAfternoonSlot(day) ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
                          <div className="bg-blue-50 rounded p-2">
                            <div className="text-xs font-semibold text-blue-800 mb-1">Vormittag</div>
                            {vmHoliday ? (
                              <div className="text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center rounded">
                                <span className="font-semibold text-orange-800">{vmHoliday.name || 'FEIERTAG'}</span>
                              </div>
                            ) : (
                              renderCell(praxis.id, date, Zeitslot.VORMITTAG)
                            )}
                          </div>
                          {hasAfternoonSlot(day) && (
                            <div className="bg-purple-50 rounded p-2">
                              <div className="text-xs font-semibold text-purple-800 mb-1">Nachmittag</div>
                              {nmHoliday ? (
                                <div className="text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center rounded">
                                  <span className="font-semibold text-orange-800">{nmHoliday.name || 'FEIERTAG'}</span>
                                </div>
                              ) : (
                                renderCell(praxis.id, date, Zeitslot.NACHMITTAG)
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Weekly Schedule Table (Desktop) */}
      <div className="hidden md:block bg-white shadow-md rounded-lg overflow-x-auto overflow-y-visible">
        <div className="min-w-max">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10">
                  Praxis
                </th>
                {weekDays.map((day, index) => (
                  <th
                    key={day}
                    colSpan={hasAfternoonSlot(day) ? 2 : 1}
                    className="border border-gray-300 px-2 py-2 text-center text-sm font-medium text-gray-700"
                  >
                    <div>{day}</div>
                    <div className="text-xs font-normal text-gray-500">
                      {formatDate(getDateForDay(index))}
                    </div>
                  </th>
                ))}
              </tr>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 sticky left-0 bg-gray-100 z-10"></th>
                {weekDays.map((day) => (
                  <React.Fragment key={`${day}-slots`}>
                    <th className="border border-gray-300 px-2 py-1 text-xs font-medium text-blue-700">
                      VM
                    </th>
                    {hasAfternoonSlot(day) && (
                      <th className="border border-gray-300 px-2 py-1 text-xs font-medium text-purple-700">
                        NM
                      </th>
                    )}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedPraxen.length === 0 ? (
                <tr>
                  <td
                    colSpan={13}
                    className="border border-gray-300 px-4 py-8 text-center text-gray-500"
                  >
                    Keine Praxen vorhanden.
                  </td>
                </tr>
              ) : (
                displayedPraxen.map((praxis) => (
                  <tr key={praxis.id}>
                    <td className="border border-gray-300 px-4 py-2 font-medium text-sm text-gray-900 sticky left-0 bg-white z-10">
                      {praxis.name}
                    </td>
                    {weekDays.map((day, dayIndex) => {
                      const date = getDateForDay(dayIndex);

                      // Check for full day holiday
                      const fullDayHoliday = feiertage.find(f => {
                        return isSameDay(f.datum, date) && f.typ === 'GANZTAGS';
                      });

                      // Check for partial holidays
                      const vmHoliday = feiertage.find(f => {
                        return isSameDay(f.datum, date) && f.typ === 'VORMITTAG';
                      });

                      const nmHoliday = feiertage.find(f => {
                        return isSameDay(f.datum, date) && f.typ === 'NACHMITTAG';
                      });

                      if (fullDayHoliday) {
                        return (
                          <td
                            key={`${praxis.id}-${day}-holiday`}
                            colSpan={hasAfternoonSlot(day) ? 2 : 1}
                            className="border border-gray-300 bg-white p-0 align-middle"
                          >
                            <div className="px-2 py-2 text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center">
                              <span className="font-semibold text-orange-800">
                                {fullDayHoliday.name || 'FEIERTAG'}
                              </span>
                            </div>
                          </td>
                        );
                      }

                      return (
                        <React.Fragment key={`${praxis.id}-${day}`}>
                          <td className="border border-gray-300 bg-blue-50">
                            {vmHoliday ? (
                              <div className="px-2 py-2 text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center">
                                <span className="font-semibold text-orange-800">{vmHoliday.name || 'FEIERTAG'}</span>
                              </div>
                            ) : (
                              renderCell(praxis.id, date, Zeitslot.VORMITTAG)
                            )}
                          </td>
                          {hasAfternoonSlot(day) && (
                            <td className="border border-gray-300 bg-purple-50">
                              {nmHoliday ? (
                                <div className="px-2 py-2 text-xs min-h-[3rem] bg-orange-100 flex items-center justify-center text-center">
                                  <span className="font-semibold text-orange-800">{nmHoliday.name || 'FEIERTAG'}</span>
                                </div>
                              ) : (
                                renderCell(praxis.id, date, Zeitslot.NACHMITTAG)
                              )}
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex gap-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-50 border border-gray-300"></div>
          <span>Vormittag (07:30 - 13:00)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-purple-50 border border-gray-300"></div>
          <span>Nachmittag (14:00 - 18:30)</span>
        </div>
      </div>

      {/* Abwesenheiten / Urlaub */}
      {abwesenheiten.length > 0 && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-red-800 font-semibold mb-2">Urlaub / Abwesenheiten:</h3>
          <div className="flex flex-wrap gap-2">
            {abwesenheiten.map((abwesenheit) => (
              <span key={abwesenheit.id} className="text-red-700 text-sm font-medium bg-white px-2 py-1 rounded border border-red-100">
                {abwesenheit.arzt
                  ? `${abwesenheit.arzt.vorname} ${abwesenheit.arzt.nachname}`
                  : 'Unbekannt'} ({abwesenheit.typ}: {formatDate(new Date(abwesenheit.startDatum))} - {formatDate(new Date(abwesenheit.endDatum))})
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyScheduleReadOnly;
